import ListItem from '@tiptap/extension-list-item'
import { Plugin, PluginKey } from '@tiptap/pm/state'

import type { Node as ProseMirrorNode } from '@tiptap/pm/model'

const listItemMarkerColorPluginKey = new PluginKey(
  'announcementListItemMarkerColor'
)

const getUniformListItemTextColor = (node: ProseMirrorNode): string | null => {
  let uniformColor: string | null = null
  let hasColoredText = false
  let hasUncoloredText = false

  node.descendants((child) => {
    if (!child.isText) {
      return
    }

    const textStyleMark = child.marks.find(
      (mark) => mark.type.name === 'textStyle'
    )
    const segmentColor = textStyleMark?.attrs.color as string | undefined

    if (!segmentColor) {
      hasUncoloredText = true
      return
    }

    hasColoredText = true

    if (uniformColor === null) {
      uniformColor = segmentColor
      return
    }

    if (uniformColor !== segmentColor) {
      uniformColor = null
    }
  })

  if (!hasColoredText || hasUncoloredText || uniformColor === null) {
    return null
  }

  return uniformColor
}

/** 行全体が同一文字色のとき、マーカー色を CSS 変数で同期（部分色付けは維持） */
export const ListItemWithColor = ListItem.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      itemColor: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.style
            .getPropertyValue('--announcement-list-item-marker-color')
            .trim() ||
          element.getAttribute('data-item-color') ||
          null,
        renderHTML: (attributes: { itemColor?: string | null }) => {
          if (!attributes.itemColor) {
            return {}
          }

          return {
            'data-item-color': attributes.itemColor,
            style: `--announcement-list-item-marker-color: ${attributes.itemColor}`,
          }
        },
      },
    }
  },

  addProseMirrorPlugins() {
    const parentPlugins = this.parent?.() ?? []

    return [
      ...parentPlugins,
      new Plugin({
        key: listItemMarkerColorPluginKey,
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((transaction) => transaction.docChanged)) {
            return null
          }

          const tr = newState.tr
          let modified = false

          newState.doc.descendants((node, pos) => {
            if (node.type.name !== 'listItem') {
              return
            }

            const nextColor = getUniformListItemTextColor(node)

            if (node.attrs.itemColor === nextColor) {
              return
            }

            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              itemColor: nextColor,
            })
            modified = true
          })

          return modified ? tr : null
        },
      }),
    ]
  },
})
